import type { PartnerProgram } from "../customer-info/useCustomerProgram";

import {
    DEFAULT_COUNTRY_CODE,
    getHashedValue,
    removeCountryCode,
    validatePhoneNumber,
} from "../../../../../shared-utils";

export const MAX_MDN_REASON = "Maximum number of MDNs checked for eligibility";

export interface CalcIsConfirmationDisabledParams {
    customerName: string | undefined;
    verificationType: "phoneNumber" | "billingAccountNumberAndPin";
    mdn: string;
    verificationFormIsValid: boolean;
    additionalDetailsFormIsValid: boolean;
    isEditingVerificationInput: boolean;
    showProgramSelection: boolean | undefined;
    customerProgram: PartnerProgram | undefined;
    mdnLookupResult: {
        hasReachedMaxMdnLookups: boolean;
        previousHashedMdnLookups: string[];
    };
    shouldSkipMdnValidation?: boolean;
}

export interface CalcIsConfirmationDisabledResult {
    isConfirmationDisabled: boolean;
    reason?: string;
}

export const calcIsConfirmationDisabled = async ({
    customerName,
    verificationType,
    mdn,
    verificationFormIsValid,
    additionalDetailsFormIsValid,
    isEditingVerificationInput,
    showProgramSelection,
    customerProgram,
    mdnLookupResult,
    shouldSkipMdnValidation = false,
}: CalcIsConfirmationDisabledParams): Promise<CalcIsConfirmationDisabledResult> => {
    if (mdnLookupResult.hasReachedMaxMdnLookups && mdn?.trim() !== "") {
        const strippedMdn = removeCountryCode(mdn);
        const currentHashedMdn = await getHashedValue(strippedMdn, "SHA-256");
        const isCurrentMdnAlreadyLookedUp = mdnLookupResult.previousHashedMdnLookups.includes(currentHashedMdn);

        if (!isCurrentMdnAlreadyLookedUp) {
            return {
                isConfirmationDisabled: true,
                reason: MAX_MDN_REASON,
            };
        }
    }

    const isDisabled = Boolean(
        !customerName ||
        (verificationType === "phoneNumber" &&
            !validatePhoneNumber(mdn, DEFAULT_COUNTRY_CODE, shouldSkipMdnValidation).valid) ||
        !verificationFormIsValid ||
        !additionalDetailsFormIsValid ||
        isEditingVerificationInput ||
        (showProgramSelection && !customerProgram),
    );

    return {
        isConfirmationDisabled: isDisabled,
    };
};
